import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { OrderCard } from "@/components/portal/OrderCard";
import { portalOrdersQuery } from "@/lib/query-registry";
import type { PortalOrder } from "@/lib/orders.functions";

export const Route = createFileRoute("/portal/")({
  component: PortalDashboard,
});

function PortalDashboard() {
  const q = useQuery({
    ...portalOrdersQuery(),
    refetchInterval: (query) => {
      const data = query.state.data as { orders?: PortalOrder[] } | undefined;
      const orders = data?.orders ?? [];
      return orders.some((o) => o.status === "processing") ? 15_000 : false;
    },
  });
  const orders = ((q.data as { orders?: PortalOrder[] } | undefined)?.orders ?? []);

  // Stripe redirects back with ?session_id=..., show success toast once and strip the param.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.has("session_id")) {
      toast.success(
        "Payment confirmed! Your audit is queued, you'll receive email updates as it progresses.",
      );
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Your orders
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track delivery status and download reports as soon as they're ready.
          </p>
        </div>
        <a
          href="https://shugert.com.mx/geo-readiness"
          className="rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground hover:bg-card"
        >
          New audit →
        </a>
      </div>

      <div className="mt-8">
        {q.isPending && (
          <div className="rounded-2xl border border-border bg-card/40 p-6 text-sm text-muted-foreground">
            Loading your orders…
          </div>
        )}
        {q.isError && (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
            {q.error?.message ?? "Couldn't load your orders."}
          </div>
        )}
        {!q.isPending && !q.isError && orders.length === 0 && (
          <div className="rounded-2xl border border-border bg-card/40 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              No audits yet, ready to find out how visible your brand is to AI?
            </p>
            <a
              href="https://shugert.com.mx/geo-readiness"
              className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white hover:opacity-90"
              style={{ backgroundColor: "#0D7680" }}
            >
              Run a GEO audit →
            </a>
          </div>
        )}
        <div className="grid gap-4">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      </div>
    </div>
  );
}